import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tooltip,
  Alert,
  Avatar,
  CircularProgress,
  Backdrop,
  Snackbar,
  Tab,
  Tabs,
  FormControlLabel,
  Switch,
  Autocomplete,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
// Firebase imports
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

export default function Contacts() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, isSuperAdmin, isMember } = useAuth();

  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteError, setDeleteError] = useState('');
  const [filters, setFilters] = useState({
    client: '',
    isPrimaryBilling: '',
    search: ''
  });
  const [formData, setFormData] = useState({
    fullName: '',
    titleRole: '',
    phoneNumber: '',
    email: '',
    associatedClient: '',
    notes: '',
    isPrimaryBillingContact: false
  });

  // Fetch clients (needed for the Associated Client field)
  const fetchClients = async () => {
    try {
      // All roles see all clients
      const q = query(collection(db, 'clients'), orderBy('clientName', 'asc'));
      const snapshot = await getDocs(q);
      const clientsData = [];
      snapshot.forEach((doc) => {
        clientsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setClients(clientsData);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Fetch contracts (needed to check if contact is assigned to active contract)
  const fetchContracts = async () => {
    try {
      const contractsRef = collection(db, 'contracts');
      const q = query(contractsRef, where('status', '==', 'Active'));
      const snapshot = await getDocs(q);
      const contractsData = [];
      snapshot.forEach((doc) => {
        contractsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setContracts(contractsData);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  // Check if contact can be deleted (not assigned to active contract)
  const canDeleteContact = (contactId) => {
    return !contracts.some(contract => 
      contract.primaryBillingContactId === contactId || 
      contract.signatureContactId === contactId
    );
  };

  // Fetch contacts based on permissions
  const fetchContacts = async () => {
    try {
      setLoading(true);
      // All roles see all contacts
      const q = query(collection(db, 'contacts'), orderBy('fullName', 'asc'));

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const contactsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          contactsData.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.()?.toISOString()?.split('T')[0] || new Date().toISOString().split('T')[0],
          });
        });
        setContacts(contactsData);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setSnackbar({ open: true, message: 'Error loading contacts', severity: 'error' });
      setLoading(false);
    }
  };

  // Add new contact
  const addContact = async (contactData) => {
    try {
      setSaving(true);
      const sanitizedData = {
        ...contactData,
        // Prevent marking as primary billing without a client
        isPrimaryBillingContact: contactData.associatedClient ? contactData.isPrimaryBillingContact : false,
      };
      
      // If this contact is set as primary billing, update others for the same client
      if (sanitizedData.isPrimaryBillingContact && sanitizedData.associatedClient) {
        await updatePrimaryBillingContact(sanitizedData.associatedClient, null);
      }

      const docRef = await addDoc(collection(db, 'contacts'), {
        ...sanitizedData,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid
      });
      
      setSnackbar({ open: true, message: 'Contact added successfully!', severity: 'success' });
      return docRef.id;
    } catch (error) {
      console.error('Error adding contact:', error);
      setSnackbar({ open: true, message: error.message || 'Error adding contact', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Update existing contact
  const updateContact = async (contactId, contactData) => {
    try {
      setSaving(true);
      const sanitizedData = {
        ...contactData,
        // Prevent marking as primary billing without a client
        isPrimaryBillingContact: contactData.associatedClient ? contactData.isPrimaryBillingContact : false,
      };
      
      // Check if contact is assigned to active contract (makes fields read-only)
      const isAssignedToContract = contracts.some(contract => 
        contract.primaryBillingContactId === contactId || 
        contract.signatureContactId === contactId
      );

      if (isAssignedToContract) {
        // Only allow notes to be updated if assigned to contract
        const updateData = {
          notes: sanitizedData.notes
        };
        await updateDoc(doc(db, 'contacts', contactId), updateData);
      } else {
        // Verify client access for Partners (only when a client is linked)
        // If this contact is set as primary billing, update others for the same client
        if (sanitizedData.isPrimaryBillingContact && sanitizedData.associatedClient) {
          await updatePrimaryBillingContact(sanitizedData.associatedClient, contactId);
        }

        await updateDoc(doc(db, 'contacts', contactId), {
          ...sanitizedData,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid
        });
      }
      
      setSnackbar({ open: true, message: 'Contact updated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error updating contact:', error);
      setSnackbar({ open: true, message: error.message || 'Error updating contact', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Update primary billing contact for a client (system-controlled)
  const updatePrimaryBillingContact = async (clientId, newPrimaryContactId) => {
    const batch = writeBatch(db);
    
    // Remove primary billing from all other contacts of this client
    const clientContacts = contacts.filter(contact => contact.associatedClient === clientId);
    clientContacts.forEach(contact => {
      if (contact.id !== newPrimaryContactId) {
        const contactRef = doc(db, 'contacts', contact.id);
        batch.update(contactRef, { isPrimaryBillingContact: false });
      }
    });

    await batch.commit();
  };

  // Delete contact with business rule validation
  const deleteContact = async (contactId) => {
    try {
      // Check if contact can be deleted
      if (isMember()) {
        throw new Error('Members cannot delete contacts.');
      }
      if (!canDeleteContact(contactId)) {
        setDeleteError('This contact is assigned to an active contract role and cannot be deleted.');
        return;
      }

      const contactRef = doc(db, 'contacts', contactId);
      await deleteDoc(contactRef);
      setSnackbar({ open: true, message: 'Contact deleted successfully!', severity: 'info' });
    } catch (error) {
      console.error('Error deleting contact:', error);
      setSnackbar({ open: true, message: error.message || 'Error deleting contact', severity: 'error' });
      throw error;
    }
  };

  useEffect(() => {
    if (currentUser) {
      const initializeData = async () => {
        await fetchClients();
        await fetchContracts();
        const unsubscribe = await fetchContacts();
        
        return () => {
          if (unsubscribe && typeof unsubscribe === 'function') {
            unsubscribe();
          }
        };
      };
      
      initializeData();
    }
  }, [currentUser]);

  // Read query param ?clientId=... to pre-filter by client
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const clientIdParam = params.get('clientId');
      if (clientIdParam) {
        setActiveTab(0);
        setFilters((prev) => ({ ...prev, client: clientIdParam }));
      }
    } catch {}
  }, [location.search]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, contacts, activeTab, clients, userProfile?.role]);

  const applyFilters = () => {
    let filtered = contacts;

    // Members don't see contacts of inactive clients
    if (isMember()) {
      filtered = filtered.filter((contact) => {
        const client = clients.find((c) => c.id === contact.associatedClient);
        return !client || client.status === 'Active';
      });
    }
    
    // Tab-based filtering
    switch (activeTab) {
      case 0: // My Contacts (Partners) / All Contacts (Super Admin)
        // Already filtered in fetchContacts
        break;
      case 1: // Billing Contacts
        filtered = filtered.filter(contact => contact.isPrimaryBillingContact === true);
        break;
      case 2: // Unlinked Contacts (system error detection)
        filtered = filtered.filter(contact => !contact.associatedClient);
        break;
      case 3: // By Client (grouped view)
        // Sort by client name
        filtered = [...filtered].sort((a, b) => {
          const clientA = clients.find(c => c.id === a.associatedClient)?.clientName || '';
          const clientB = clients.find(c => c.id === b.associatedClient)?.clientName || '';
          return clientA.localeCompare(clientB);
        });
        break;
    }
    
    // Additional filters
    if (filters.client) {
      filtered = filtered.filter(contact => contact.associatedClient === filters.client);
    }
    if (filters.isPrimaryBilling !== '') {
      filtered = filtered.filter(contact => 
        contact.isPrimaryBillingContact === (filters.isPrimaryBilling === 'true')
      );
    }
    if (filters.search) {
      const searchLower = String(filters.search).toLowerCase();
      filtered = filtered.filter((contact) => {
        const fullName = String(contact.fullName || '').toLowerCase();
        const email = String(contact.email || '').toLowerCase();
        const titleRole = String(contact.titleRole || '').toLowerCase();
        return (
          fullName.includes(searchLower) ||
          email.includes(searchLower) ||
          titleRole.includes(searchLower)
        );
      });
    }
    
    setFilteredContacts(filtered);
  };

  const handleOpenDialog = (contact = null) => {
    setDeleteError(''); // Clear any previous delete errors
    
    if (contact) {
      setEditingContact(contact);
      setFormData({
        fullName: contact.fullName,
        titleRole: contact.titleRole || '',
        phoneNumber: contact.phoneNumber || '',
        email: contact.email || '',
        associatedClient: contact.associatedClient,
        notes: contact.notes || '',
        isPrimaryBillingContact: contact.isPrimaryBillingContact || false
      });
    } else {
      setEditingContact(null);
      setFormData({
        fullName: '',
        titleRole: '',
        phoneNumber: '',
        email: '',
        associatedClient: '',
        notes: '',
        isPrimaryBillingContact: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingContact(null);
    setDeleteError('');
  };

  const handleSubmit = async () => {
    try {
      if (!formData.fullName) {
        setSnackbar({ open: true, message: 'Full Name is required', severity: 'error' });
        return;
      }
      if (formData.isPrimaryBillingContact && !formData.associatedClient) {
        setSnackbar({ open: true, message: 'Link a Client to set as Billing Contact', severity: 'error' });
        return;
      }

      if (editingContact) {
        await updateContact(editingContact.id, formData);
      } else {
        await addContact(formData);
      }
      
      handleCloseDialog();
    } catch (error) {
      // Error handling is done in the respective functions
    }
  };

  const handleDelete = async (id) => {
    setDeleteError(''); // Clear previous errors
    
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await deleteContact(id);
      } catch (error) {
        // Error handling is done in the deleteContact function
      }
    }
  };

  const getClientName = (clientId) => {
    if (!clientId) {
      return 'Unlinked Contact';
    }
    const client = clients.find(c => c.id === clientId);
    return client ? client.clientName : 'Unknown Client';
  };

  // Check if contact is assigned to active contract (affects editability)
  const isContactAssignedToContract = (contactId) => {
    return contracts.some(contract => 
      contract.primaryBillingContactId === contactId || 
      contract.signatureContactId === contactId
    );
  };

  // Tab labels
  const getTabLabels = () => {
    if (isSuperAdmin()) {
      return ['All Contacts', 'Billing Contacts', 'Unlinked Contacts', 'By Client'];
    } else {
      return ['My Contacts', 'Billing Contacts', 'Unlinked Contacts', 'By Client'];
    }
  };

  // Compute Active clients for selection and detect if currently selected client is inactive
  const activeClients = clients.filter((client) => client.status === 'Active');
  const selectedClient = clients.find((client) => client.id === formData.associatedClient);
  const selectedClientIsInactive = Boolean(selectedClient && selectedClient.status !== 'Active');

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
     

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Contacts Module
            </Typography>
 
          </Box>
        </Box>
        <Box>
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 1 }}
          >
            Filters
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Contact
          </Button>
        </Box>
      </Box>

      {/* No Clients Info */}
      {clients.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>No Clients Available</Typography>
          <Typography variant="body2">
            You currently have no Clients. You can still add contacts and link them to a Client later.
          </Typography>
        </Alert>
      )}

      {/* Delete Error Alert */}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDeleteError('')}>
          <Typography variant="body2">{deleteError}</Typography>
        </Alert>
      )}

      {/* Tabs for different views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          {getTabLabels().map((label, index) => (
            <Tab key={index} label={label} />
          ))}
        </Tabs>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search by name, email, or title"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  value={filters.client}
                  label="Client"
                  onChange={(e) => setFilters({ ...filters, client: e.target.value })}
                >
                  <MenuItem value="">All Clients</MenuItem>
                  {(isMember() ? clients.filter((c) => c.status === 'Active') : clients).map(client => (
                    <MenuItem key={client.id} value={client.id}>{client.clientName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Primary Billing Contact</InputLabel>
                <Select
                  value={filters.isPrimaryBilling}
                  label="Primary Billing Contact"
                  onChange={(e) => setFilters({ ...filters, isPrimaryBilling: e.target.value })}
                >
                  <MenuItem value="">All Contacts</MenuItem>
                  <MenuItem value="true">Primary Billing Only</MenuItem>
                  <MenuItem value="false">Non-Primary Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Contacts Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Full Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Phone </TableCell>
                <TableCell>Email</TableCell>
                <TableCell> Client</TableCell>
                <TableCell> Billing</TableCell>
                {/* <TableCell>Contract Status</TableCell> */}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredContacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="h6" color="text.secondary">No contacts found.</Typography>
                    {activeTab === 2 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Contacts not yet linked to a Client.
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredContacts.map((contact) => (
                  <TableRow key={contact.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                          {contact.fullName?.charAt(0) || '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {contact.fullName || 'Unknown Contact'}
                          </Typography>
                          {contact.notes && (
                            <Typography variant="caption" color="text.secondary">
                              Has notes
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{contact.titleRole || '-'}</TableCell>
                    <TableCell>
                      {contact.phoneNumber && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          {contact.phoneNumber}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          {contact.email}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        {getClientName(contact.associatedClient)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {contact.isPrimaryBillingContact && (
                        <Chip 
                           color="primary"
                          size="small"
                          icon={<StarIcon />}
                        />
                      )}
                    </TableCell>
                    {/* <TableCell>
                      {isContactAssignedToContract(contact.id) ? (
                        <Chip 
                          label="Assigned to Contract" 
                          color="warning"
                          size="small"
                          icon={<WarningIcon />}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">Available</Typography>
                      )}
                    </TableCell> */}
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(contact)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {!isMember() && (
                        <Tooltip title={canDeleteContact(contact.id) ? "Delete" : "Cannot delete - assigned to contract"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(contact.id)}
                              color="error"
                              disabled={!canDeleteContact(contact.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Contact' : 'Add Contact'}
        </DialogTitle>
        <DialogContent>
          {editingContact && isContactAssignedToContract(editingContact.id) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This contact is assigned to an active contract. Only notes can be edited.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
                disabled={editingContact && isContactAssignedToContract(editingContact.id)}
                helperText="First and last name of the individual"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title / Role"
                value={formData.titleRole}
                onChange={(e) => setFormData({ ...formData, titleRole: e.target.value })}
                disabled={editingContact && isContactAssignedToContract(editingContact.id)}
                placeholder="e.g. Billing Contact, CTO, Founder"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                disabled={editingContact && isContactAssignedToContract(editingContact.id)}
                placeholder="Mobile or office number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={editingContact && isContactAssignedToContract(editingContact.id)}
                placeholder="Business or personal email address"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Associated Client</InputLabel>
                <Select
                  value={formData.associatedClient}
                  label="Associated Client"
                  onChange={(e) => setFormData({ ...formData, associatedClient: e.target.value })}
                  disabled={editingContact && isContactAssignedToContract(editingContact.id)}
                >
                  <MenuItem value="">None (Unlinked)</MenuItem>
                  {editingContact && selectedClientIsInactive && selectedClient && (
                    <MenuItem value={selectedClient.id} disabled>
                      {selectedClient.clientName} (Inactive)
                    </MenuItem>
                  )}
                  {activeClients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.clientName}
                    </MenuItem>
                  ))}
                </Select>
                {formData.isPrimaryBillingContact && !formData.associatedClient && (
                  <FormHelperText error>Link a Client to mark as Billing Contact</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPrimaryBillingContact}
                    onChange={(e) => setFormData({ ...formData, isPrimaryBillingContact: e.target.checked })}
                    disabled={editingContact && isContactAssignedToContract(editingContact.id)}
                  />
                }
                label="Billing Contact"
              />
            
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Internal notes visible only to UCENA staff"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              saving || !formData.fullName || (formData.isPrimaryBillingContact && !formData.associatedClient)
            }
          >
            {editingContact ? 'Update' : 'Add'}
            {saving && <CircularProgress size={24} color="inherit" />}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary Cards */}
 
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={saving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
} 